import React, { useState, useEffect, useMemo } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { secureDelete, secureGet, secureGetPdf } from "../../config/api";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { formatDateTime, formatAmount } from "../../utils/format";
import ProductRow from "../../components/ProductRow";
import DeletePopup from "../../components/DeletePopup";
import SnackbarNotification from "../../components/SnackbarNotification";
import LoadingLogo from "../../components/LoadingLogo";
import Breadcrumbs from "../../components/Breadcrumbs";

import trashIcon from "../../icons/trashW.svg";
import exportIcon from "../../icons/export.svg";
import editIcon from "../../icons/editB.svg";

const QuoteDetails = () => {
    const history = useHistory();
    const location = useLocation();
    const { id } = useParams();
    const { t } = useTranslation();
    const [quote, setQuote] = useState({});
    const [loading, setLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const showSnackbar = (message, severity = "success") => {
        setSnackbar({ open: true, message, severity });
    };

    const abortController = useMemo(() => new AbortController(), []);
    const { signal } = abortController;


    useEffect(() => {
        const fetchQuote = async () => {
            setLoading(true);
            try {
                const response = await secureGet(`/quotes/${id}`, showSnackbar, { signal });
                setQuote(response.data);
            } catch (error) {
                if (error.name !== "AbortError") {
                    console.error("Failed to fetch quote:", error);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchQuote();
        return () => abortController.abort();
    }, [id, abortController, signal]);

    const orderExist = quote.cart && quote.cart !== null;



    const renderStatut = () => (
        <div className="flex items-center ">
            <span
                className={"px-[5px] py-1 rounded-[10px] font-semibold text-[12px] bg-[#0AD69F] text-white "}
            >
                DV
            </span>

            <div className="w-2 h-[2px] bg-[#043F88]"></div>

            <span
                className={`px-[5px] py-1 rounded-[10px] font-semibold text-[12px] ${orderExist ? "bg-[#0AD69F] text-white cursor-pointer" : "bg-[#EF476F] text-white cursor-not-allowed"
                    }`}
                onClick={() => handleStatutClick("BC")}
            >
                BC
            </span>
        </div>
    );


    const handleStatutClick = (key) => {
        switch (key) {
            case "BC":
                handleConsultBC();
                break;
            default:
                break;
        }
    };

    const handleConsultBC = () => {
        history.push(`/orders/${quote.cart.id}`);
    }

    const handleDeleteClick = () => {
        setDeletePopupVisible(true);
    };

    const handleDeleteConfirm = async () => {
        setDeleting(true);
        try {
            await secureDelete(`/quotes/delete/${quote.id}`, showSnackbar);
            setDeletePopupVisible(false);
            history.push({
                pathname: "/quotes",
                state: {
                    showSnackbar: true,
                    snackbarMessage: t(`confirmation.quoteDelete`),
                    snackbarSeverity: 'success',
                    filters: location.state?.filters || {},
                    page: location.state?.page || 0,
                    size: location.state?.size || 30,
                },
            });
        } catch (error) {
            console.error("Error deleting the quote:", error);
        } finally {
            setDeleting(false);
            setDeletePopupVisible(false);
        }
    };

    const handleDownloadClick = async () => {
        setDownloading(true);
        try {
            const response = await secureGetPdf(`/quotes/pdf/${quote.id}`, showSnackbar);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `Quote_${quote.id}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            showSnackbar(t("confirmation.downloadSuccess"), "success");
        } catch (error) {
            console.error("Failed to download quote:", error);
        } finally {
            setDownloading(false);
        }
    };

    const handleEdit = () => {
        history.push({
            pathname: `/quotes/details/modify/${quote.id}`,
            state: { filters: location.state?.filters || {}, page: location.state?.page || 0, size: location.state?.size || 30 },
        });
    };


    const renderActionButtons = () => {
        return (
            <>
                <button
                    className="flex items-center shadow-[0px_20px_80px_#00000011] justify-center bg-white text-[#043F88] px-6 py-3 font-bold text-[14px] rounded-[12px] gap-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => handleDownloadClick()}
                    disabled={downloading}
                >
                    {downloading ? (<><div className="w-5 h-5 border-2 border-[#043F88] border-t-transparent rounded-full animate-spin" /><span>{t(`actions.saving`) || 'Chargement...'}</span></>) : (<><img src={exportIcon} alt="delete" className="w-5 h-5" />{t(`actions.downloadQuote`)}</>)}
                </button>
                {!orderExist && (
                    <button
                        className="flex items-center shadow-[0px_20px_80px_#00000011] justify-center bg-white text-[#043F88] px-6 py-3 font-bold text-[14px]  rounded-[12px] gap-x-2 "
                        onClick={handleEdit}>
                        <img src={editIcon} alt="" className="w-5 h-5" />

                        {t(`actions.modify`)}
                    </button>
                )}
                <button
                    className="flex items-center shadow-[0px_20px_80px_#00000011] justify-center bg-[#EF476F] text-[#FFFFFF] px-6 py-3 font-bold text-[14px] rounded-[12px] gap-x-2 "
                    onClick={handleDeleteClick}>
                    <img src={trashIcon} alt="" className="w-5 h-5" />

                    {t(`actions.delete`)}
                </button>
            </>
        );
    };

    if (loading) return <LoadingLogo />;

    return (
        <div>
            <Helmet>
                <title>GrayFox - {t("titles.quote_details")}</title>
            </Helmet>
            <div className="mb-4">
                <Breadcrumbs />
                <h1 className="text-[40px] font-semibold text-[#000000]">
                    {t("titles.quote_details")}
                </h1>
                <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-[10px]">
                        <p className="text-[#00000] font-semibold text-[24px]">
                            {quote.quoteNumber}
                        </p>

                        <div className="flex space-x-1">{renderStatut()}</div>
                    </div>

                    <div className="flex space-x-4">
                        {renderActionButtons(quote.id)}
                    </div>
                </div>

                <div>
                    <p className="text-[#38485C] text-[20px] font-semibold">
                        {quote.client?.code} - {quote.client?.name}
                    </p>
                </div>
            </div>

            <div className="w-full flex justify-between bg-white py-4 pl-[32px] rounded-[12px] shadow-[0px_6px_26px_rgba(98,107,125,0.07)] mb-4">
                <div className="flex-1">
                    <p className="text-[#707070] text-[14px] font-semibold mb-1">{t("rows.creationDate")}</p>
                    <p className="text-[#38485C] text-[20px] font-semibold">{formatDateTime(quote.createdAt)}</p>
                </div>
                <div className="flex-1">
                    <p className="text-[#707070] text-[14px] font-semibold mb-1">
                        {t(`rows.commercial`)}
                    </p>
                    <p className="text-[#38485C] text-[20px] font-semibold">
                        {quote.user?.firstName} {quote.user?.lastName}
                    </p>
                </div>
                <div className="flex-1">
                    <p className="text-[#707070] text-[14px] font-semibold mb-1">STOCK</p>
                    <p className="text-[#38485C] text-[20px] font-semibold">
                        {quote.repo?.name}
                    </p>
                </div>
                <div className="flex-1">
                    <p className="text-[#707070] text-[14px] font-semibold mb-1">TOTAL HT</p>
                    <p className="text-[#38485C] text-[20px] font-semibold">{formatAmount(quote.priceHT)}</p>
                </div>
                <div className="flex-1">
                    <p className="text-[#707070] text-[14px] font-semibold mb-1">TOTAL TTC</p>
                    <p className="text-[#38485C] text-[20px] font-semibold">{formatAmount(quote.priceTTC)}</p>
                </div>
            </div>

            <h3 className="text-[16px] font-medium text-[#ACAFC6] mb-4">{t("fields_types.list")}</h3>
            <div className="space-y-2">
                {quote.details?.map((product, index) => (
                    <ProductRow key={index} product={product} />
                ))}
            </div>

            {isDeletePopupVisible && (
                <DeletePopup open={isDeletePopupVisible} onCancel={() => setDeletePopupVisible(false)} onConfirm={handleDeleteConfirm} loading={deleting} />
            )}

            <SnackbarNotification open={snackbar.open} message={snackbar.message} severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} />
        </div>
    );
};

export default QuoteDetails;
